import { useState } from 'react';

import { format } from 'date-fns';

import { Alert, Box, Button, Paragraph, TextButton, Toast, useToast } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertyAvailabilityWebApiHandler_confirm,
  useRentalManagerUserPropertySummaryWebApiHandler_listing,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import ListingAvailabilityModal from './ListingAvailabilityModal';

import type { UserPropertyAvailabilityConfirmResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  listingAlias: string;
  expirationMs: number;
};

const ListingAvailability = ({ listingAlias, expirationMs }: Props) => {
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();

  const [displayLearnMoreModal, setDisplayLearnMoreModal] = useState(false);

  const { data: listingData } = useRentalManagerUserPropertySummaryWebApiHandler_listing(
    { listingAlias },
    { enabled: Boolean(listingAlias) },
  );

  const { mutate: confirmListingAvailability } = useRentalManagerUserPropertyAvailabilityWebApiHandler_confirm({
    meta: {
      context: {
        api: 'rental-manager',
        details: 'useRentalManagerUserPropertyAvailabilityWebApiHandler_confirm',
      },
      invalidates: ['users-properties-availability-confirm'],
    },
    onError: () => {
      if (displayLearnMoreModal) {
        setDisplayLearnMoreModal(false);
      }
      enqueueToast(<Toast tone="critical">Listing availability couldn’t be confirmed. Try again.</Toast>, {
        duration: 10000,
      });
    },
    onSuccess: (response: UserPropertyAvailabilityConfirmResponseWebDto) => {
      queryClient.invalidateQueries({
        queryKey: useRentalManagerUserPropertySummaryWebApiHandler_listing.getKey({ listingAlias }),
      });

      if (displayLearnMoreModal) {
        setDisplayLearnMoreModal(false);
      }

      enqueueToast(
        <Toast tone="success">
          {`Listing active until ${format(new Date(response.newExpirationMs), 'MM/dd/yy')}`}
        </Toast>,
        { duration: 10000 },
      );
    },
  });

  const handleLearnMoreClick = () => {
    setDisplayLearnMoreModal(true);
  };

  return (
    <Alert tone="warning">
      <Box
        css={{
          display: 'flex',
          flexDirection: { base: 'column', xxl: 'row' },
          alignItems: { base: 'flex-start', xxl: 'center' },
          justifyContent: { base: 'flex-start', xxl: 'space-between' },
        }}
      >
        <Box>
          <Paragraph textStyle="body-bold">Is this listing still available?</Paragraph>
          <Paragraph textStyle="body" css={{ marginBottom: { base: '200', xxl: 0 } }}>
            {`Listing expires ${format(new Date(expirationMs), 'MMM dd, yyyy')}`}
          </Paragraph>
        </Box>

        <Box
          css={{
            display: 'flex',
            flexDirection: { base: 'column', xxl: 'row' },
            alignItems: { base: 'flex-start', xxl: 'center' },
          }}
        >
          <Box css={{ display: 'flex', order: { base: 1, xxl: 2 } }}>
            <Button
              css={{ marginBottom: { lgDown: '200' }, marginRight: { lgDown: '400' } }}
              tone="brand"
              emphasis="filled"
              size="sm"
              onClick={() => confirmListingAvailability({ getParams: { listingAlias } })}
            >
              Confirm availability
            </Button>
          </Box>
          <Box css={{ display: 'flex', order: { base: 2, xxl: 1 } }}>
            <TextButton onClick={handleLearnMoreClick} textStyle="body-sm-bold" css={{ marginRight: { xxl: '400' } }}>
              Learn more
            </TextButton>
          </Box>
        </Box>
      </Box>

      {displayLearnMoreModal && (
        <ListingAvailabilityModal
          onConfirmClick={() => confirmListingAvailability({ getParams: { listingAlias } })}
          onModalClose={() => setDisplayLearnMoreModal(false)}
          activeProduct={listingData?.paidInclusion?.activeProduct}
        />
      )}
    </Alert>
  );
};

export default ListingAvailability;
